import {
  DatabasesExternalAssetsDisplayGridRowData,
  TablesExternalAssetsDisplayGridRowData
} from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import { ActivitySpinnerProps, ActivitySpinnerSize } from '@tableau/widgets';
import * as _ from 'underscore';

import { ProfilePictureStyles } from '../../app/content/styles/ProfilePictureStyles';
import { HeaderIconButton } from '../../app/header/HeaderIconButton';
import { vizHeaderSearchButtonImgTestId } from '../../app/layout/PageHeaderSearch';
import { vizHeaderSearchOpenButtonTestId } from '../../app/layout/VizViewerPageHeader';
import { searchTelemetry } from '../../app/spaces/Search/SearchTelemetry';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Formatters, Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { Projects } from '../../projects/resources/Projects';
import { Views } from '../../views/resources/Views';
import { getMergedMetricInfo } from '../../metrics';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { MetricDataPoints } from '../../metrics/resources/MetricDataPoints';
import { Metrics } from '../../metrics/resources/Metrics';
import { Users } from '../../userManagement/services/resources/Users';
import { FeatureFlags } from '../FeatureFlags';
import { ListResult, mapListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { Keys } from '../helpers/Keys';
import { Order } from '../helpers/Order';
import { ContentFiltersKeys } from '../models/ContentFilters';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IDataSourceInfo } from '../models/IDataSourceInfo';
import { IProjectInfo } from '../models/IProjectInfo';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookInfo } from '../models/IWorkbookInfo';
import { OrderKeys } from '../models/OrderKeys';
import { sortOptionsBySearchSubspace } from '../models/sort/SortOptionDefinitions';
import { Module } from '../Module';
import { Databases } from '../resources/Databases';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { PublishedConnections } from '../resources/PublishedConnections';
import { IResourceQuery } from '../resources/ResourceQuery';
import { Tables } from '../resources/Tables';
import { Workbooks } from '../resources/Workbooks';
import { IMetricDataList } from '../ServerApi/types/IMetricDataList';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IUserWithPublicInfo } from '../ServerApi/types/IUser';
import { ServerService } from '../services/ServerService';
import { ContentDraftStatus } from '../ServerApi/types/ContentDraft';

export interface IOmniboxExternalScope extends ng.IScope {
  closed: (event: BaseJQueryEventObject) => void;
  closedWithoutNeedingFocus: (event: BaseJQueryEventObject) => void;
  isSearchOpen: boolean;
}

export interface IMetricInfoForOmni extends IMetricInfo {
  value?: string;
}

export interface IPublishedConnectionInfoForOmni extends IPublishedConnectionInfo {
  value?: string;
}

export interface CatalogOmniboxItem {
  readonly id: string;
  readonly name: string;
  readonly isCertified: boolean;
  readonly workbookCount: number;
  readonly datasourceCount: number;
}

function mapCatalogItem(item: TablesExternalAssetsDisplayGridRowData | DatabasesExternalAssetsDisplayGridRowData): CatalogOmniboxItem {
  return {
    id: item.id,
    name: item.name,
    isCertified: item.isCertified,
    workbookCount: item.downstreamWorkbooksConnection ? item.downstreamWorkbooksConnection.totalCount : 0,
    datasourceCount: item.downstreamDatasourcesConnection ? item.downstreamDatasourcesConnection.totalCount : 0,
  };
}

export interface IOmniboxScope extends ng.IScope {
  closed: (locals: { event: BaseJQueryEventObject }) => void;
  closedWithoutNeedingFocus: (locals: { event: BaseJQueryEventObject }) => void;

  searchOpen: boolean;
  searchText: string;
  showResults: boolean;
  showNoResults: boolean;
  showPending: boolean;
  showCatalogingError: boolean;
  selectedIndex: number;
  maxResultsPerType: number;
  textBoxName: string;
  userProfilePictureSize: number;
  userProfilePictureFontSize: number;
  activitySpinnerProps: ActivitySpinnerProps;

  datasources: ListResult<IDataSourceInfo>;
  flows: ListResult<FlowListItemInfo>;
  projects: ListResult<IProjectInfo>;
  publishedConnections: ListResult<IPublishedConnectionInfoForOmni>;
  users: ListResult<IUserWithPublicInfo>;
  views: ListResult<IViewInfo>;
  metrics: ListResult<IMetricInfoForOmni>;
  workbooks: ListResult<IWorkbookInfo>;
  dataRoles: ListResult<IDataRoleInfo>;

  tables: ListResult<CatalogOmniboxItem>;
  databases: ListResult<CatalogOmniboxItem>;
  tablesReturnedError: boolean;
  databasesReturnedError: boolean;

  popupMaxHeight: () => number;
  close: (isInternallyTriggered: boolean, $event?: BaseJQueryEventObject, navigateToSearchSpace?: boolean) => any;
  isItemSelected: (type: string, index: number) => boolean;
  sendTelemetryEvent: (type: string, seeAll: boolean, index: number, total: number, contentId: string) => void;
  selectItem: (type: string, index: number) => void;
  itemHref: (state: string, params: any) => string;
  keydown: ($event: JQueryKeyEventObject) => any;
  seeAllResultsMessage: () => string;
  seeAllHref: (state: string, type: string) => string;
  showSeeAll: (list: ListResult<any>, type: string) => boolean;
  tooltipForDataSource: (datasource: IDataSourceInfo) => string;
  tooltipForView: (view: IViewInfo) => string;
  tooltipForWorkbook: (workbook: IWorkbookInfo) => string;
  tooltipForFlow: (flow: FlowListItemInfo) => string;
  tooltipForPublishedConnection: (flow: IPublishedConnectionInfoForOmni) => string;
  clearSearch: () => void;
  handleFocus: () => void;

  selectedId: () => string;
  createItemId: (type: string, index: number) => string;
  totalNumberOfSections: () => number;
}

export class OmniboxCtrl {

  public static MaxResultsPerType = 3;
  public static InputDebounceMilliseconds = 200;
  private static SpaceLeftWhenMaxHeight = 100;
  private static TextBoxName = 'omniboxTextBox';
  private static GetUsersExtraParams = {
    'getItemCounts': true
  };

  private currentRequestProgress: {
    pendingRequests: number;
    totalResults: number;
  };
  private selectedIndex: number;
  private maxSelectedIndex: number;
  private startIndexForType: _.Dictionary<number>;
  private isUnmountingOmni: boolean;

  public static $inject = [
    '$scope',
    '$translate',
    '$window',
    'ServerService',
    'DataSources',
    'DataRoles',
    'Flows',
    'Workbooks',
    'Tables',
    'Databases',
    'PublishedConnections'
  ];
  $onInit: () => void;

  constructor(
    private $scope: IOmniboxScope,
    private $translate: ng.translate.ITranslateService,
    private $window: ng.IWindowService,
    private server: ServerService,
    private datasources: DataSources,
    private dataRoles: DataRoles,
    private flows: Flows,
    private workbooks: Workbooks,
    private tables: Tables,
    private databases: Databases,
    private publishedConnections: PublishedConnections,
  ) {
    this.$onInit = () => {
      this.startIndexForType = {};
      this.isUnmountingOmni = false;

      const debouncedUpdate = _.debounce(() => {
        this.$scope.$evalAsync(() => {
          if (!this.isUnmountingOmni) {
            this.updateResults();

            if (this.$scope.searchText) {
              searchTelemetry.sendSearchTermUpdatedEvent('omni', this.$scope.searchText);
            }
          }
        });
      }, OmniboxCtrl.InputDebounceMilliseconds);
      this.$scope.$watch('searchText', () => {
        debouncedUpdate();
      });

      // this handles closing the omnibox by mouse-down clicks and keyboard presses outside the omnibox
      // as well as internal keys and clicks.
      this.$scope.close = (isInternallyTriggered: boolean, $event?: BaseJQueryEventObject, navigateToSearchSpace?: boolean) => {
        let isCloseIcon = false;
        if ($event) {
          const targetElement = $($event.target);
          const isTextBox = targetElement.attr('name') === OmniboxCtrl.TextBoxName;
          const isButton = targetElement.closest('#' + HeaderIconButton.searchBoxIconButtonId).length > 0;
          isCloseIcon = targetElement.attr('data-tb-test-id') === vizHeaderSearchOpenButtonTestId
            || targetElement.attr('data-tb-test-id') === vizHeaderSearchButtonImgTestId;
          if (isTextBox || isButton && $scope.searchOpen) {
            return;
          }
        }
        if (this.$scope.searchText.length > 0 && !navigateToSearchSpace) {
          searchTelemetry.endSearchFlowWithNoResultEvent('omni', this.$scope.searchText);
        }
        this.$scope.clearSearch();
        this.updateResults();
        if (isCloseIcon || isInternallyTriggered) {
          // if the close icon was activated with the keyboard or clicked, we want to close the omnibox and return focus
          // to the search glass button
          this.$scope.closed({ event: $event });
        } else {
          // if something else triggered closing the omnibox by a keyboard press, then we want to close the
          // omnibox without resetting focus to the search glass button
          this.$scope.closedWithoutNeedingFocus({ event: $event });
        }
      };

      this.$scope.$on('destroy', () => {
        // sometimes search results return after we have navigated away (usually to the search space). We don't
        // want to show the results omnibox without the search box because it looks weird, so we mark that the
        // search box is unmounting and know to ignore the returned results.
        this.isUnmountingOmni = true;
      });

      this.$scope.createItemId = (type: string, index: number) => {
        return `${type}-${index}`;
      };

      this.$scope.selectedId = () => {
        if (this.$scope.showNoResults || this.$scope.showResults || this.$scope.showPending) {
          if (this.selectedIndex === 0) return 'global';

          let type = '';
          if (this.selectedIndex < this.startIndexForType['metrics']) type = 'views';
          else if (this.selectedIndex < this.startIndexForType['workbooks']) type = 'metrics';
          else if (this.selectedIndex < this.startIndexForType['datasources']) type = 'workbooks';
          else if (this.selectedIndex < this.startIndexForType['flows']) type = 'datasources';
          else if (this.selectedIndex < this.startIndexForType['projects']) type = 'flows';
          else if (this.selectedIndex < this.startIndexForType['publishedConnections']) type = 'projects';
          else if (this.selectedIndex < this.startIndexForType['tables']) type = 'publishedConnections';
          else if (this.selectedIndex < this.startIndexForType['databases']) type = 'tables';
          else if (this.selectedIndex < this.startIndexForType['dataRoles']) type = 'databases';
          else if (this.selectedIndex < this.startIndexForType['users']) type = 'dataRoles';
          else type = 'users';

          return `${type}-${this.selectedIndex - this.startIndexForType[type]}`;
        }
        return '';
      };

      this.$scope.totalNumberOfSections = (): number => {
        if (this.$scope.showNoResults || this.$scope.showResults || this.$scope.showPending) {
          let numSections = 1;
          if (!_.isUndefined(this.$scope.views) && this.$scope.views.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.metrics) && this.$scope.metrics.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.workbooks) && this.$scope.workbooks.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.projects) && this.$scope.projects.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.publishedConnections) && this.$scope.publishedConnections.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.flows) && this.$scope.flows.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.datasources) && this.$scope.datasources.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.dataRoles) && this.$scope.dataRoles.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.databases) && this.$scope.databases.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.tables) && this.$scope.tables.total > 0) numSections++;
          if (!_.isUndefined(this.$scope.users) && this.$scope.users.total > 0) numSections++;

          return numSections;
        }

        return 0;
      };

      this.$scope.keydown = ($event: JQueryKeyEventObject) => {
        switch ($event.which) {
          case Keys.ArrowUp:
            if (this.selectedIndex > 0) {
              this.selectedIndex--;
            } else {
              this.selectedIndex = this.maxSelectedIndex;
            }
            this.scrollSelectedItemIntoView();
            break;
          case Keys.ArrowDown:
            if (this.selectedIndex < this.maxSelectedIndex) {
              this.selectedIndex++;
            } else {
              this.selectedIndex = Math.min(0, this.maxSelectedIndex);
            }
            this.scrollSelectedItemIntoView();
            break;
          case Keys.Enter:
            if (this.selectedIndex >= 0) {

              const selectedElement = $('.tb-omnibox-results, .tb-omnibox-pending').find('.tb-selected');
              if (_.isUndefined(selectedElement) || _.isUndefined(selectedElement[0]) || selectedElement.hasClass('tb-disabled')) {
                this.isUnmountingOmni = true;
                const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });
                const path = urls.searchViewsUrl() + '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(this.$scope.searchText);
                Location.instance.setUrl(path);
                this.$scope.close(false); // want to return focus to search bar so use close()
                break;
              }

              let dataset = selectedElement[0].dataset;

              this.$scope.sendTelemetryEvent(
                dataset.type,
                dataset.seeAll === 'true',
                Number(dataset.index),
                Number(dataset.total),
                dataset.contentId);

              let href = selectedElement.attr('href');
              Location.instance.setUrl(href);
              this.$scope.close(true, $event); // want to return focus to search bar so use close()

            } else {  // enter inside search box, not inside omnibox
              this.isUnmountingOmni = true;
              const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });
              const path = urls.searchViewsUrl() + '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(this.$scope.searchText);
              Location.instance.setUrl(path);
              this.$scope.close(true); // want to return focus to search bar so use close()
            }

            break;
          case Keys.Escape:
            this.$scope.close(true); // want to return focus to search bar so use close()
            break;
        }
      };

      this.$scope.isItemSelected = (type: string, index: number): boolean => {
        return this.startIndexForType[type] + index === this.selectedIndex;
      };

      this.$scope.seeAllResultsMessage = (): string => {
        return Messages.omniboxSeeAllResults({ SEARCH_TERM: this.$scope.searchText });
      };

      this.$scope.sendTelemetryEvent = (type: string, seeAll: boolean, index: number, total: number, contentId: string): void => {
        searchTelemetry.sendSearchResultClickedEvent({
          type: TelemetryEvents.SearchResultClicked,
          eventSource: 'omni',
          resultType: type,
          seeAll: seeAll,
          index: index,
          total: total,
          searchTerm: this.$scope.searchText,
          contentId: contentId,
        });
      };

      this.$scope.selectItem = (type: string, index: number): void => {
        if (!type || !_.isNumber(index)) return;
        this.selectedIndex = this.startIndexForType[type] + index;
      };

      this.$scope.itemHref = (type: string, params: any): string => {
        const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });

        let href = '#';
        if (type === 'datasource') {
          href = urls.datasourceUrl(params.id);
        } else if (type === 'flow') {
          if (params.draftStatus === ContentDraftStatus.unpublished) {
            href = urls.flowEditorDraftUrl(params.draftLuid);
          } else {
            href = urls.flowUrl(params.id);
          }
        } else if (type === 'project') {
          href = urls.projectUrl(params.id);
        } else if (type === 'view') {
          href = urls.viewUrl(params.path);
        } else if (type === 'workbook') {
          href = urls.workbookUrl(params.id);
        } else if (type === 'siteUser') {
          href = urls.userUrl(params.username, params.domainName);
        } else if (type === 'table') {
          href = urls.catalogTableUrl(params.id);
        } else if (type === 'database') {
          href = urls.catalogDatabaseUrl(params.id);
        } else if (type === 'dataRole') {
          href = urls.dataRoleUrl(params.id);
        } else if (type === 'metric') {  // double check this
          href = urls.metricUrl(params.id);
        } else if (type === 'publishedConnection') {
          href = urls.publishedConnectionUrl(params.id);
        }

        return href;
      };

      const andOrderByRelevance = '&' + UrlParamKeys.Order + '=' + Order.serialize([Order.desc(OrderKeys.relevance)]);

      this.$scope.seeAllHref = (type: string): string => {

        // get the base url
        let seeAllHref = this.seeAllUrlForType(type);

        // Add the search query to the url
        seeAllHref += '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(this.$scope.searchText);

        return seeAllHref;
      };

      this.$scope.showSeeAll = (list: ListResult<any>, type: string): boolean => {
        return type !== 'users' || !this.server.isLimitedUserVisibilityEnforced();
      };
      this.$scope.tooltipForDataSource = (datasource: IDataSourceInfo): string => {
        return this.$translate.instant('itemWithProject', { ITEM: datasource.name, PROJECT: datasource.project.name });
      };
      this.$scope.tooltipForView = (view: IViewInfo): string => {
        return this.$translate.instant('viewWithWorkbook', { VIEW: view.name, WORKBOOK: view.workbook.name });
      };
      this.$scope.tooltipForWorkbook = (workbook: IWorkbookInfo): string => {
        return this.$translate.instant('itemWithProject', { ITEM: workbook.name, PROJECT: workbook.project.name });
      };
      this.$scope.tooltipForFlow = (flow: FlowListItemInfo): string => {
        return this.$translate.instant('itemWithProject', { ITEM: flow.name, PROJECT: flow.project.name });
      };
      this.$scope.tooltipForPublishedConnection = (publishedConnection: IPublishedConnectionInfoForOmni): string => {
        return this.$translate.instant('itemWithProject', { ITEM: publishedConnection.name, PROJECT: publishedConnection.project.name });
      };
      this.$scope.maxResultsPerType = OmniboxCtrl.MaxResultsPerType;

      const windowElement = $(this.$window);
      this.$scope.popupMaxHeight = () => {
        return windowElement.height() - OmniboxCtrl.SpaceLeftWhenMaxHeight;
      };

      this.$scope.clearSearch = () => this.$scope.searchText = '';

      this.$scope.textBoxName = OmniboxCtrl.TextBoxName;

      this.$scope.userProfilePictureSize = ProfilePictureStyles.Sizes.SmallIconSize;

      this.$scope.userProfilePictureFontSize = ProfilePictureStyles.FontSizes.SmallIconFontSize;

      this.$scope.activitySpinnerProps = {
        testId: 'omnibox-pending-spinner',
        spinnerSize: ActivitySpinnerSize.Small
      };

      this.$scope.handleFocus = () => {
        if (!this.$scope.searchText) {
          searchTelemetry.startSearchFlow('omni');
        }
      };
    };
  }

  // the type string parameter is hardcoded in the omniboxContentAnalytics.pug
  private seeAllUrlForType(type: string) {
    const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });
    if (type === 'datasources') {
      return urls.searchDatasourcesUrl();
    } else if (type === 'flows') {
      return urls.searchFlowsUrl();
    } else if (type === 'projects') {
      return urls.searchProjectsUrl();
    } else if (type === 'views' || type === 'global') {
      return urls.searchViewsUrl();
    } else if (type === 'workbooks') {
      return urls.searchWorkbooksUrl();
    } else if (type === 'users') {
      return urls.searchUsersUrl();
    } else if (type === 'tables') {
      return urls.searchTablesUrl();
    } else if (type === 'databases') {
      return urls.searchDatabasesUrl();
    } else if (type === 'dataRoles') {
      return urls.searchDataRolesUrl();
    } else if (type === 'metrics') {
      return urls.searchMetricsUrl();
    } else if (type === 'publishedConnections') {
      return urls.searchPublishedConnectionsUrl();
    }

    return urls.searchViewsUrl();
  }

  private updateAriaLiveText(totalResults: number) {
    const ariaLiveRegion = $('.tb-omnibox-status');
    let text = '';
    if (ariaLiveRegion) {
      if (this.$scope.showPending) {
        text = Messages.omniboxFetchingResults();
      } else if (this.$scope.showNoResults) {
        text = Messages.omniboxShowingNoResults();
      } else if (this.$scope.showResults) {
        text = Messages.omniboxShowingResults({
          TOTAL_COUNT: totalResults,
          TOTAL_COUNT_LOC: Formatters.formatCount(totalResults)
        });
      }

      ariaLiveRegion.text(text);
    }
  }

  private updateResults() {
    if (this.$scope.searchText) {
      const requestProgress = {
        pendingRequests: this.getPendingRequestsCount(),
        totalResults: 0
      };
      this.currentRequestProgress = requestProgress;

      // if already showing results, don't add the spinner (e.g., if the omnibox is open as the user types)
      // but if the omnibox isn't open yet, show a spinner while we fetch results
      this.$scope.showPending = this.$scope.showResults || this.$scope.showNoResults ? false : true;

      if (this.$scope.showPending) {
        this.updateAriaLiveText(0);
      }

      // This will close the search panel when clicking on an iframe
      // (NLP tab and the viz) because window will lose focus when clicking into the iframe
      setTimeout(() => {
        window.addEventListener('blur', this.onWindowBlur);
      });

      const updateProgress = (result: ListResult<any>) => {
        if (this.currentRequestProgress === requestProgress && !this.isUnmountingOmni) {
          requestProgress.totalResults += result.total;
          requestProgress.pendingRequests--;
          if (requestProgress.pendingRequests === 0) {

            this.currentRequestProgress = null;
            this.$scope.showResults = (requestProgress.totalResults > 0);
            this.$scope.showNoResults = !this.$scope.showResults;
            this.$scope.showPending = false;
            this.$scope.showCatalogingError = this.$scope.tablesReturnedError || this.$scope.databasesReturnedError;

            this.updateStartIndexes();
            // we want the index to be -1 if we're not showing results so we don't hit enter on something that isn't showing
            // (we don't highlight the 'no results' text)
            this.selectedIndex = (this.$scope.showResults || this.$scope.showPending) ? 0 : -1;
            this.maxSelectedIndex = requestProgress.totalResults > 0
              ? this.startIndexForType['users'] + this.itemsForType(this.$scope.users) - 1
              : -1;

            this.updateAriaLiveText(requestProgress.totalResults);

            // make sure we rerender the results
            this.$scope.$applyAsync();
          }
        }
      };
      this.updateType<IProjectInfo>(
        Projects.all(),
        sortOptionsBySearchSubspace['projects'].defaultOrder,
        (result: ListResult<IProjectInfo>) => {
          this.$scope.projects = result;
          updateProgress(result);
        }
      );
      if (FeatureFlags.publishedConnectionContentManagementEnabled) {
        this.updateType<IPublishedConnectionInfoForOmni>(
          this.publishedConnections.all(),
          sortOptionsBySearchSubspace['publishedConnections'].defaultOrder,
          (result: ListResult<IPublishedConnectionInfo>) => {
            this.$scope.publishedConnections = result;
            updateProgress(result);
          }
        );
      }
      if (!this.server.isLimitedUserVisibilityEnforced()) {
        this.updateType<IUserWithPublicInfo>(
          Users.instance.all().withExtraParams(OmniboxCtrl.GetUsersExtraParams),
          [Order.asc(OrderKeys.displayName)],
          (result: ListResult<IUserWithPublicInfo>) => {
            this.$scope.users = result;
            updateProgress(result);
          }
        );
      }
      this.updateType<IViewInfo>(
        Views.all().withExtraParams({ forSearchPage: true }),
        sortOptionsBySearchSubspace['views'].defaultOrder,
        (result: ListResult<IViewInfo>) => {
          this.$scope.views = result;
          updateProgress(result);
        }
      );
      if (FeatureFlags.metricsSpaceEnabled) {
        this.updateType<IMetricInfoForOmni>(
          Metrics.all(),
          sortOptionsBySearchSubspace['metrics'].defaultOrder,
          (result: ListResult<IMetricInfo>) => {
            this.$scope.metrics = result;
            updateProgress(result);

            if (result.total > 0) {
              this.getMetricValues(result);
            }
          }
        );
      }
      this.updateType<IWorkbookInfo>(
        this.workbooks.all().withExtraParams({ forSearchPage: true }),
        sortOptionsBySearchSubspace['workbooks'].defaultOrder,
        (result: ListResult<IWorkbookInfo>) => {
          this.$scope.workbooks = result;
          updateProgress(result);
        }
      );
      this.updateType<IDataSourceInfo>(
        this.datasources.published(),
        sortOptionsBySearchSubspace['datasources'].defaultOrder,
        (result: ListResult<IDataSourceInfo>) => {
          this.$scope.datasources = result;
          updateProgress(result);
        }
      );
      if (this.server.flowsOnSiteEnabled()) {
        this.updateType<FlowListItemInfo>(
          this.flows.all(),
          sortOptionsBySearchSubspace['flows'].defaultOrder,
          (result: ListResult<FlowListItemInfo>) => {
            this.$scope.flows = result;
            updateProgress(result);
          }
        );
      }
      if (this.server.catalogingOnSiteEnabled()) {
        const tablesResourceQuery = this.tables.all();
        tablesResourceQuery
          .addFilter(Filter.search(this.$scope.searchText))
          .getSlice(0, OmniboxCtrl.MaxResultsPerType)
          .then((result: ListResult<TablesExternalAssetsDisplayGridRowData>) => {
            this.$scope.tablesReturnedError = false;
            this.$scope.tables = mapListResult(result, mapCatalogItem);
            updateProgress(result);

          }).catch((reason: any) => {
            // Data Catalog might down, indexing, ingesting, etc. so just show 0 results and an error message.
            const emptyList = ListResult.empty<CatalogOmniboxItem>();
            this.$scope.tables = emptyList;
            this.$scope.tablesReturnedError = true;

            updateProgress(emptyList);
          });

        const databasesResourceQuery = this.databases.all();

        databasesResourceQuery
          .addFilter(Filter.search(this.$scope.searchText))
          .getSlice(0, OmniboxCtrl.MaxResultsPerType)
          .then((result: ListResult<DatabasesExternalAssetsDisplayGridRowData>) => {
            this.$scope.databasesReturnedError = false;
            this.$scope.databases = mapListResult(result, mapCatalogItem);
            updateProgress(result);

          }).catch((reason: any) => {
            // Data Catalog might down, indexing, ingesting, etc. so just show 0 results and an error message.
            const emptyList = ListResult.empty<CatalogOmniboxItem>();
            this.$scope.databases = emptyList;
            this.$scope.databasesReturnedError = true;

            updateProgress(emptyList);
          });
      }

      this.updateType<IDataRoleInfo>(
        this.dataRoles.all(),
        sortOptionsBySearchSubspace['dataRoles'].defaultOrder,
        (result: ListResult<IDataRoleInfo>) => {
          this.$scope.dataRoles = result;
          updateProgress(result);
        }
      );
    } else {
      this.currentRequestProgress = undefined;

      this.$scope.datasources = ListResult.empty<IDataSourceInfo>();
      this.$scope.flows = ListResult.empty<FlowListItemInfo>();
      this.$scope.projects = ListResult.empty<IProjectInfo>();
      this.$scope.publishedConnections = ListResult.empty<IPublishedConnectionInfoForOmni>();
      this.$scope.users = ListResult.empty<IUserWithPublicInfo>();
      this.$scope.views = ListResult.empty<IViewInfo>();
      this.$scope.metrics = ListResult.empty<IMetricInfoForOmni>();
      this.$scope.workbooks = ListResult.empty<IWorkbookInfo>();
      this.$scope.tables = ListResult.empty<CatalogOmniboxItem>();
      this.$scope.databases = ListResult.empty<CatalogOmniboxItem>();
      this.$scope.dataRoles = ListResult.empty<IDataRoleInfo>();
      this.$scope.showResults = false;
      this.$scope.showNoResults = false;
      this.$scope.showPending = false;
      this.$scope.showCatalogingError = false;
      this.updateStartIndexes();
      this.selectedIndex = 0;
      this.maxSelectedIndex = 0;
    }
  }

  private getMetricValues(result: ListResult<IMetricInfo>) {
    const ids: string[] = result.items.map((item: IMetricInfo) => item.id);

    MetricDataPoints.getMetricDataPoints(ids)
      .then((metricDataList: IMetricDataList) => {

        for (let index in this.$scope.metrics.items) {
          let metric: IMetricInfoForOmni = this.$scope.metrics.items[index];
          const data = metricDataList.data[metric.id];

          if (data) {
            metric = getMergedMetricInfo(metric, metricDataList);

            // update metric in array
            this.$scope.metrics.items[index] = metric;
          } // otherwise, no need to update
        }
      });
  }

  private getPendingRequestsCount() {
    let total = 5;
    if (this.server.catalogingOnSiteEnabled()) {
      total = total + 2;  // 1 for tables, 1 more for databases
    }

    if (this.server.flowsOnSiteEnabled()) {
      total = total + 1;
    }

    if (!this.server.isLimitedUserVisibilityEnforced()) {
      total = total + 1;  // users call to happen only if limited user visbility is not enforced
    }

    if (FeatureFlags.metricsSpaceEnabled) {
      total = total + 1;
    }

    if (FeatureFlags.publishedConnectionContentManagementEnabled) {
      total = total + 1;
    }

    return total;
  }

  private scrollSelectedItemIntoView() {
    if (this.selectedIndex < 0) {
      return;
    }

    const viewPortElement = $('.tb-omnibox-results, .tb-omnibox-pending');
    const selectedItemElement = $(viewPortElement.find('.tb-omnibox-menu-item, .tb-omnibox-section-header, .tb-omnibox-section-global-see-all')[this.selectedIndex]);

    // leave space for the header/margin at top and bottom of the list
    const menuItemHeight = selectedItemElement.height();
    const currentScroll = viewPortElement.scrollTop() - menuItemHeight;
    const viewPortTop = 0;
    const viewPortLength = viewPortElement.height() - menuItemHeight - menuItemHeight;

    const itemTop = selectedItemElement.position().top;
    const itemBottom = itemTop + menuItemHeight;

    if (itemTop < viewPortTop) {
      viewPortElement.scrollTop(currentScroll + itemTop);
    } else if (itemBottom > viewPortLength) {
      viewPortElement.scrollTop(currentScroll + itemBottom - viewPortLength);
    }
  }

  private updateType<T>(query: IResourceQuery<T>, order: IOrderClause[], updateFn: (result: ListResult<T>) => any) {
    query
      .addFilter(Filter.search(this.$scope.searchText))
      .withOrder(order)
      .getSlice(0, OmniboxCtrl.MaxResultsPerType)
      .then((result: ListResult<T>) => {
        updateFn(result);
      });
  }

  private updateStartIndexes() {
    this.startIndexForType['global'] = 0;
    this.startIndexForType['views'] = 1;
    this.startIndexForType['metrics'] = this.startIndexForType['views'] + this.itemsForType(this.$scope.views);
    this.startIndexForType['workbooks'] = this.startIndexForType['metrics'] + this.itemsForType(this.$scope.metrics);
    this.startIndexForType['datasources'] = this.startIndexForType['workbooks'] + this.itemsForType(this.$scope.workbooks);
    this.startIndexForType['flows'] = this.startIndexForType['datasources'] + this.itemsForType(this.$scope.datasources);
    this.startIndexForType['projects'] = this.startIndexForType['flows'] + this.itemsForType(this.$scope.flows);
    this.startIndexForType['publishedConnections'] = this.startIndexForType['projects'] + this.itemsForType(this.$scope.projects);
    this.startIndexForType['tables'] = this.startIndexForType['publishedConnections'] + this.itemsForType(this.$scope.publishedConnections);
    this.startIndexForType['databases'] = this.startIndexForType['tables'] + this.itemsForType(this.$scope.tables);
    this.startIndexForType['dataRoles'] = this.startIndexForType['databases'] + this.itemsForType(this.$scope.databases);
    this.startIndexForType['users'] = this.startIndexForType['dataRoles'] + this.itemsForType(this.$scope.dataRoles);
  }

  private itemsForType(list: ListResult<any> | null | undefined): number {
    if (list) {
      let items = list.items ? list.items.length : 0;
      if (items > 0) {
        items++;  // add one for the section header
      }
      return items;
    } else {
      return 0;
    }
  }

  private onWindowBlur = () => {
    window.removeEventListener('blur', this.onWindowBlur);
    this.$scope.close(false);
  }
}

Module.instance().controller('OmniboxCtrl', OmniboxCtrl);
